/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Portal data epics
 */

import { Injectable } from '@angular/core';

import {
  rx,
  Observable,
  observableOf,
  observableEMPTY,
  observableForkJoin,
} from '@shared/rx';
import { flatMap, get, head, isEmpty, omit, set } from 'lodash';
import { combineEpics, Epic, StateObservable } from 'redux-observable';
import { XprtDataActions, SessionActions, UiActions } from '../../actions';
import { IAction, IEpicsService, IUserPermissions } from '@shared/types';
import {
  XprtDataService,
  PermissionService,
  PostMessageService,
  DmDataService,
} from '@shared/services';
import { IAppState } from '@app/store';
import { IBaseXPRTResponse } from '@shared/types/entities/xprt-entities/base-xprt.type';
import { PreferencesActions } from '@app/store/actions/preferences/preferences.actions';
import { CurrencySchemeService } from '@app/shared/services/currency-scheme/currency-scheme.service';
import { COMPANY_PREFERENCES_IS_MISSING } from '@app/text-content/error-page-templates';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class XprtDataEpics implements IEpicsService {
  // eslint-disable-next-line max-params
  public constructor(
    private xprtDataService: XprtDataService,
    private permissionService: PermissionService,
    private postMessageService: PostMessageService,
    private dmDataService: DmDataService,
    private currencySchemeService: CurrencySchemeService,
    public uiActions: UiActions,
    public sessionActions: SessionActions,
  ) {}

  public getEpic(): Epic<any, any> {
    return combineEpics(
      this.fetchPreferencesForCompany.bind(this),
      this.fetchCompanies.bind(this),
      this.fetchUserPermissions.bind(this),
      this.fetchInfo.bind(this),
      this.requestParentAccessToken.bind(this),
      this.showEventSelector.bind(this),
      this.fetchStores.bind(this),
    );
  }

  private requestParentAccessToken(
    action$: Observable<IAction>,
  ): Observable<any> {
    return action$.pipe(
      rx.filter((action) =>
        [XprtDataActions.GET_ACCESS_TOKEN].includes(action.type),
      ),
      rx.mergeMap((action: IAction) => {
        this.postMessageService.sendMessage(XprtDataActions.GET_ACCESS_TOKEN);

        return observableEMPTY;
      }),
    );
  }

  private fetchCompanies(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) =>
        [XprtDataActions.FETCH_COMPANIES].includes(action.type),
      ),
      rx.mergeMap((action: IAction) => {
        return this.xprtDataService
          .fetchCompanies(action.payload.companyIds)
          .pipe(
            rx.map((servicePayload) =>
              XprtDataActions.fetchCompaniesSuccessAction(servicePayload),
            ),
            rx.catchError((errors) =>
              observableOf(XprtDataActions.fetchCompaniesErrorAction(errors)),
            ),
          );
      }),
    );
  }

  private fetchInfo(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) =>
        [XprtDataActions.FETCH_XPRT_INFO].includes(action.type),
      ),
      rx.mergeMap(() => {
        return this.xprtDataService.getEnvironmentInfo().pipe(
          rx.map((servicePayload) =>
            XprtDataActions.fetchInfoSuccessAction(servicePayload),
          ),
          rx.catchError((errors) =>
            observableOf(XprtDataActions.fetchInfoErrorAction(errors)),
          ),
        );
      }),
    );
  }

  private fetchUserPermissions(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) =>
        [SessionActions.FETCH_PERMISSIONS].includes(action.type),
      ),
      rx.mergeMap((action: IAction) => {
        /**
         * fetching permissions for each required component and
         * storing observables in one array
         */
        const permissionObsArr: Observable<IUserPermissions>[] = [
          this.permissionService.fetchUserPermissions(),
        ];

        /**
         * joining all observables with permissions into one and then
         * combine all responses into one which will be put in the redux store
         */
        return observableForkJoin(permissionObsArr).pipe(
          rx.map((responseList: IUserPermissions[]) => {
            const items = flatMap(responseList, 'items');
            const payload = {
              items,
              total: items.length,
            };

            return SessionActions.fetchPermissionsSuccessAction(payload);
          }),
          rx.catchError((errors) =>
            observableOf(SessionActions.fetchPermissionsErrorAction(errors)),
          ),
        );
      }),
    );
  }

  private showEventSelector(action$: Observable<IAction>): Observable<never> {
    return action$.pipe(
      rx.filter((action) =>
        [XprtDataActions.SHOW_EVENT_SELECTOR].includes(action.type),
      ),
      rx.mergeMap((action: IAction) => {
        this.postMessageService.sendMessage(
          XprtDataActions.SHOW_EVENT_SELECTOR,
          action.payload.messageId,
          omit(action.payload, 'messageId'),
        );

        return observableEMPTY;
      }),
    );
  }

  private fetchStores(
    action$: Observable<IAction>,
    store: StateObservable<IAppState>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [XprtDataActions.FETCH_STORES].includes(action.type),
      ),
      rx.mergeMap((action: IAction) => {
        return this.xprtDataService.fetchStores(action.payload.query).pipe(
          rx.map((servicePayload: IBaseXPRTResponse) => {
            // filter sites which person can manage
            const state: IAppState = store.value;
            const currentCompanyId = state.userSession.currentCompanyId;
            servicePayload.items = servicePayload.items.filter(
              (item) => item.company.id === currentCompanyId,
            );

            return XprtDataActions.fetchStoresSuccessAction(servicePayload);
          }),
          rx.catchError((errors) =>
            observableOf(XprtDataActions.fetchStoresErrorAction(errors)),
          ),
        );
      }),
    );
  }

  /**
   * Method for catching FETCH_PREFERENCES_FOR_COMPANY action type
   * and calling entity's service method
   *
   * @param action$ - action Observable
   * @returns
   */
  private fetchPreferencesForCompany(
    action$: Observable<IAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [PreferencesActions.FETCH_PREFERENCES_FOR_COMPANY].includes(
          action.type,
        ),
      ),
      rx.mergeMap(({ payload }: IAction) => {
        return this.dmDataService
          .fetchPreferencesByUserToken(payload.preferences)
          .pipe(
            rx.concatMap((response) => {
              // TODO: need to make this change while we
              // receive preferences from API with unnecessary fields
              const isCompanyIdExist = get(response, 'company_id', null);
              if (isEmpty(response) || !isCompanyIdExist) {
                this.uiActions.showErrorPage(COMPANY_PREFERENCES_IS_MISSING);

                return observableOf(
                  PreferencesActions.fetchPreferencesForCompanyErrorAction(
                    {
                      title:
                        I18n.ErrorMessages
                          .CanNotReceivePreferencesForCurrentCompany,
                    },
                    payload.options,
                  ),
                );
              }

              // fetch translation for the locale set in preferences
              this.sessionActions.fetchTranslations();

              const mapResponse = (resp): Observable<IAction> => {
                const currencySchemeEntityId = get(
                  resp,
                  'preferences.regional_settings.default_currency_scheme_entity_id',
                );

                return this.currencySchemeService
                  .fetchList({
                    filter: `(is_active eq in(true) and entity_id eq '${currencySchemeEntityId}' and is_master eq in(true))`,
                  })
                  .pipe(
                    rx.map((currencyScheme) => {
                      set(
                        resp,
                        'preferences.regional_settings.default_currency_scheme_entity',
                        head(currencyScheme?.items),
                      );

                      return PreferencesActions.fetchPreferencesForCompanySuccessAction(
                        resp,
                        payload.options,
                      );
                    }),
                  );
              };

              return mapResponse(response);
            }),
            rx.catchError((errors) => {
              this.uiActions.showErrorPage({
                title: errors?.error?.title || errors?.meta?.code,
                icon: 'glyphicon-warning-sign',
                description: errors?.error?.stack || errors?.stack,
              });

              return observableOf(
                PreferencesActions.fetchPreferencesForCompanyErrorAction(
                  errors,
                  payload.options,
                ),
              );
            }),
          );
      }),
    );
  }
}
